import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../contexts/PusherContext';
import { useThreadLoading } from '../../contexts/ThreadLoadingContext';
import { AgentEvents, WebRTCCallRequestedEvent } from '@dreamai/types';
import { ReactComponent as CallingIcon } from '../../icons/webrtc-calls/calling.svg';
import api from '../../api';
import './call-notification.scss';

interface CallNotification {
  threadId: string;
  agentName?: string;
  message?: string;
  timestamp: number;
}

const CallNotification: React.FC = () => {
  const [activeCall, setActiveCall] = useState<CallNotification | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAccessTokenSilently } = useAuth0();
  const userId = user?.sub;
  const pusherContext = usePusher(true);
  const pusher = pusherContext?.pusherInstance;
  const {
    registerWebRTCCallDismissedCallback,
    unregisterWebRTCCallDismissedCallback,
    dismissWebRTCCall,
    registerWebRTCCallAcceptedCallback,
    unregisterWebRTCCallAcceptedCallback,
  } = useThreadLoading();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    // Create audio element for call notification sound
    // Place your call-ring.mp3 or call-ring.wav file in /public/sounds/
    audioRef.current = new Audio('/call-ring.mp3');
    audioRef.current.loop = true; // Loop the ring sound

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.error('Error playing call notification sound:', error);
      });
    }
  }, []);

  const stopSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const handleCallRequest = useCallback(
    (data: WebRTCCallRequestedEvent) => {
      console.log('Global call notification received:', data);

      // Set active call notification
      setActiveCall({
        threadId: data.threadId,
        agentName: data.agentName,
        message: data.message,
        timestamp: Date.now(),
      });

      // Play sound
      playSound();
    },
    [playSound],
  );

  useEffect(() => {
    if (!pusher || !userId) return;

    // Subscribe to the user's private channel for global call notifications
    // Replace | with - to match backend channel naming
    const sanitizedUserId = userId.replace('|', '-');
    const channelName = `${process.env.REACT_APP_PUSHER_ENV}-private-user-${sanitizedUserId}`;
    const userChannel = pusher.subscribe(channelName);

    userChannel.bind(
      AgentEvents.WEBRTC_CALL_REQUESTED,
      (data: WebRTCCallRequestedEvent) => {
        handleCallRequest(data);
      },
    );

    return () => {
      userChannel.unbind(AgentEvents.WEBRTC_CALL_REQUESTED);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, userId, handleCallRequest]);

  // Register callback to listen for call dismissed events from other components
  useEffect(() => {
    const callbackId = 'call-notification-dismissed';

    registerWebRTCCallDismissedCallback(callbackId, (threadId: string) => {
      // If the dismissed call matches our active call, stop sound and clear notification
      if (activeCall && activeCall.threadId === threadId) {
        console.log('Call dismissed from another component, cleaning up');
        stopSound();
        setActiveCall(null);
      }
    });

    return () => {
      unregisterWebRTCCallDismissedCallback(callbackId);
    };
  }, [
    activeCall,
    stopSound,
    registerWebRTCCallDismissedCallback,
    unregisterWebRTCCallDismissedCallback,
  ]);

  // Register callback to listen for call accepted events from other components
  useEffect(() => {
    const callbackId = 'call-notification-accepted';

    registerWebRTCCallAcceptedCallback(callbackId, (threadId: string) => {
      // If the accepted call matches our active call, stop sound and clear notification
      if (activeCall && activeCall.threadId === threadId) {
        console.log('Call accepted from another component, cleaning up');
        stopSound();
        setActiveCall(null);
      }
    });

    return () => {
      unregisterWebRTCCallAcceptedCallback(callbackId);
    };
  }, [
    activeCall,
    stopSound,
    registerWebRTCCallAcceptedCallback,
    unregisterWebRTCCallAcceptedCallback,
  ]);

  const handleAccept = useCallback(() => {
    if (!activeCall) return;

    stopSound();
    setActiveCall(null);

    // Navigate to the thread with the call
    navigate(`/conversations?thread_id=${activeCall.threadId}`);
  }, [activeCall, navigate, stopSound]);

  const handleDismiss = useCallback(async () => {
    if (!activeCall) return;

    stopSound();
    setActiveCall(null);

    // Notify other components that the call was dismissed
    dismissWebRTCCall(activeCall.threadId);

    // Notify backend that user declined the call
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/webrtc/decline-call',
        {
          thread_id: activeCall.threadId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error('Error declining call:', error);
      // Silent fail - user already dismissed the notification
    }
  }, [activeCall, stopSound, getAccessTokenSilently, dismissWebRTCCall]);

  // Auto-dismiss after 30 seconds
  useEffect(() => {
    if (!activeCall) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [activeCall, handleDismiss]);

  if (!activeCall) return null;

  // Check if we're currently on the thread that's calling
  const currentThreadId = searchParams.get('thread_id');
  const isOnActiveThread = currentThreadId === activeCall.threadId;

  // If we're on the active thread, don't show the notification (but sound still plays)
  if (isOnActiveThread) return null;

  return (
    <div className='call-notification-wrapper'>
      <div className='call-notification'>
        <div className='call-notification-icon'>
          <CallingIcon />
        </div>
        <div className='call-notification-content'>
          <div className='call-notification-title'>
            {activeCall.agentName || 'Agent'} is calling
          </div>
          {activeCall.message && (
            <div className='call-notification-message'>
              {activeCall.message}
            </div>
          )}
        </div>
        <div className='call-notification-actions'>
          <button className='call-accept-button' onClick={handleAccept}>
            Answer
          </button>
          <button className='call-dismiss-button' onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallNotification;
